import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Fingerprint, Snowflake, Wifi, ShieldCheck } from "lucide-react";

const THEMES = {
  aurora: {
    from: "#00F0FF",
    via: "#7A5CFF",
    to: "#FF007F",
  },
  sunset: {
    from: "#FFE600",
    via: "#FF7A00",
    to: "#FF007F",
  },
  matrix: {
    from: "#00FF9D",
    via: "#00C2FF",
    to: "#0047FF",
  },
  molten: {
    from: "#FF007F",
    via: "#FF4D00",
    to: "#FFE600",
  },
};

export const CARD_THEMES = Object.keys(THEMES);

function formatSig(chunk) {
  return chunk.match(/.{1,4}/g)?.join(" ") || chunk;
}

export default function VirtualCryptoCard({ card, revealedSecret = null, compact = false }) {
  const theme = THEMES[card.color_theme] || THEMES.aurora;
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 200, damping: 20 });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const secretDisplay = revealedSecret ? formatSig(revealedSecret) : "•••• •••• •••• ••••";

  return (
    <div style={{ perspective: 1200 }} className="w-full">
      <motion.div
        data-testid="virtual-card"
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative w-full aspect-[1.586/1] rounded-[24px] overflow-hidden shadow-2xl"
      >
        {/* Gradient base */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${theme.from} 0%, ${theme.via} 50%, ${theme.to} 100%)`,
          }}
        />
        {/* Holographic texture */}
        <div
          className="absolute inset-0 mix-blend-overlay opacity-60"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1548350896-fafc317d3ceb)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Glass sheen */}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-white/20" />
        <div className="absolute -inset-1 grain opacity-20" />

        {/* Frozen overlay */}
        {card.frozen && (
          <div className="absolute inset-0 z-30 backdrop-blur-sm bg-black/40 flex flex-col items-center justify-center gap-2">
            <Snowflake className="w-8 h-8 text-white" />
            <span className="font-mono-sig text-xs tracking-[0.3em] uppercase text-white">Frozen</span>
          </div>
        )}

        {/* Content */}
        <div className="relative z-20 h-full p-6 md:p-7 flex flex-col justify-between text-white" style={{ transform: "translateZ(40px)" }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-6 h-6 drop-shadow" strokeWidth={2} />
              <div className="flex flex-col leading-none">
                <span className="font-display font-black text-lg tracking-tighter text-glow">SIGNET</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Wifi className="w-5 h-5 rotate-90 opacity-90" />
                <span className="font-mono-sig text-[10px] tracking-[0.2em] uppercase text-glow">{card.network}</span>
              </div>
              {card.weight && (
                <div data-testid="card-face-weight" className="flex items-baseline gap-1.5">
                  <span className="font-display font-black text-base leading-none text-glow">{card.weight.score}</span>
                  <span className="font-mono-sig text-[8px] tracking-[0.25em] uppercase text-white/75">{card.weight.class}</span>
                </div>
              )}
              {card.source === "SIGNET" && (
                <div
                  data-testid="card-authenticity-badge"
                  className="flex items-center gap-1 rounded-full bg-black/25 backdrop-blur-sm px-2 py-0.5 border border-white/20"
                >
                  <ShieldCheck className="w-3 h-3 text-white/90" strokeWidth={2.5} />
                  <span className="font-mono-sig text-[8px] tracking-[0.2em] uppercase text-white/90">
                    {card.verified ? "SIGNET Verified" : "SIGNET"} · {card.reality || "Unreal"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Chip */}
          <div className="flex items-center gap-3 -mt-2">
            <div className="w-11 h-8 rounded-md bg-gradient-to-br from-yellow-200 to-yellow-500 border border-yellow-100/40 shadow-inner" />
          </div>

          {/* Card number */}
          {card.card_number && (
            <div>
              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-white/70 mb-1">Card Number</p>
              <p data-testid="card-number-display" className="font-mono-sig text-base md:text-xl tracking-[0.22em] text-glow text-white">
                {formatSig(card.card_number)}
              </p>
            </div>
          )}

          {/* Signature */}
          <div>
            <p className="font-mono-sig text-[9px] tracking-[0.35em] uppercase text-white/70 mb-1.5">
              Crypto Signature · 32 Digit
            </p>
            <div className="font-mono-sig text-sm md:text-lg tracking-[0.15em] text-glow flex flex-wrap gap-x-3 gap-y-1">
              <span className="text-white">{formatSig(card.signature_public)}</span>
              <span
                data-testid="signature-secret-display"
                className={revealedSecret ? "text-white" : "text-white/50"}
              >
                {secretDisplay}
              </span>
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-white/70 mb-1">Cardholder</p>
              <p className="font-semibold tracking-wide text-glow text-sm md:text-base">{card.cardholder_name}</p>
            </div>
            <div className="text-right">
              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-white/70 mb-1">Expires</p>
              <p className="font-mono-sig font-medium text-glow text-sm md:text-base">{card.expiry}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
